import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import moment from "moment";
import { StripeSubscriptionForm } from "./stripeSubscriptionForm";
import { listOfSubscriptions, plansEnvConfig } from "../../common/envConfig";
import {
  dateExtractFromSub,
  discountedPrice,
  planProrationEligibility,
} from "./utils/discountedPrice";
import { FREE_TRIAL_PROMO_CODES } from "./constants/freeTrialPromocodes";
import { CouponInput } from "./components/CouponInput";
import SubscriptionDetails from "./components/SubscriptionDetails";
import { RiMastercardLine, RiVisaLine } from "react-icons/ri";
import { FaPen } from "react-icons/fa6";
import Swal from "sweetalert2";
import axios from "axios";
import admin, {
  axiosWithToken,
  functionBaseUrl,
  stripeKey,
} from "../../common/firebase";
import Spinner from "../shared/spinner/Spinner";
import { userService } from "../../services/user";

const stripePromise = loadStripe(stripeKey);

function StripeSubscriptionComponent({
  email,
  productId,
  buttonText,
  handleResult,
  startDate,
  hideCoupon = false,
  freeTrial,
  trialEnd,
  showDetails = true,
  amount,
  fullName,
  handlePrice,
  addOn,
}) {
  const [coupon, setCoupon] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [subscriptionAmount, setSubscriptionAmount] = useState(-1);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [showFreeTrialMessage, setShowFreeTrialMessage] = useState(false);
  const [couponSuccess, setCouponSuccess] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(null);
  const [card, setCard] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const [loadingInit, setLoadingInit] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const planPrices = ["15.99", "79.99", "110.00", "799.99"];

  // ---- helper: create subscription with "active sub" retry logic ----
  const createSubscriptionWithRetry = async (input, retry = true) => {
    const endPoint = productId.includes("foresight")
      ? "/api/stripePartnerSub"
      : "/api/stripeCreateSubscription";

    try {
      const response = await axios(functionBaseUrl + endPoint, {
        method: "POST",
        data: input,
      });

      // Success
      if (response.status === 200) {
        return response;
      }

      // Non-200 but no error thrown
      const msg =
        response?.data?.message ||
        response?.data?.error ||
        response?.data?.reason ||
        "";

      const msgLower = (msg || "").toLowerCase();

      if (retry && msgLower.includes("active subscription")) {
        // cancel existing and retry once
        await userService.cancelCurrentSubscription(email);
        return createSubscriptionWithRetry(input, false);
      }

      return response;
    } catch (error) {
      console.log(error);

      // Try to inspect backend error shape (if any)
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "";
      const msgLower = (msg || "").toLowerCase();

      if (retry && msgLower.includes("active subscription")) {
        // cancel existing and retry once
        await userService.cancelCurrentSubscription(email);
        return createSubscriptionWithRetry(input, false);
      }

      // Re-throw so caller can handle generic error UI
      throw error;
    }
  };

  // --- INIT: only prepare data (saved card + initial coupon), NO subscription creation ---
  const init = async () => {
    try {
      // Load saved payment method if allowed
      if (useSavedCard || useSavedCard === null) {
        const responseMethods = await axios(
          functionBaseUrl + "/api/stripePaymentMethods",
          {
            method: "POST",
            data: { email: email },
          }
        ).catch((error) => {
          console.log(error);
        });

        if (responseMethods?.data?.paymentMethods?.data?.length > 0) {
          let cardData = responseMethods?.data?.paymentMethods?.data[0];
          const cardExpiry = moment(
            cardData?.card?.exp_month + "/" + cardData?.card?.exp_year,
            "MM/YYYY"
          ).valueOf();
          if (cardExpiry > moment().valueOf()) {
            setCard({
              brand: cardData.card.brand.toUpperCase(),
              lastFour: cardData.card.last4,
            });
            setUseSavedCard(true);
            setPaymentMethod(cardData?.id);
          } else {
            setUseSavedCard(false);
          }
        }
      }

      // Pre-fill coupon from REFERRALCODE (for UI)
      let referralCode = "";
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("REFERRALCODE")) {
        referralCode = urlParams.get("REFERRALCODE");
        setCoupon(referralCode);
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        title:
          '<p style="font-size:70%;">An unexpected error occurred. Please try again.</p>',
        icon: "warning",
        confirmButtonText: "Ok",
        allowOutsideClick: true,
        customClass: {
          container: "my-swal",
        },
      });
    } finally {
      setLoadingInit(false);
    }
  };

  // --- SUBSCRIBE: create subscription on click (for both saved card and new card) ---
  const subscribe = async () => {
    try {
      setIsSubmitting(true);

      let input = { email, productId, coupon, addOn };

      // Saved card flow
      if (paymentMethod) {
        input.paymentMethod = paymentMethod;
      }

      // Auto-apply coupon based on user (GP1876)
      await axiosWithToken(`${functionBaseUrl}/api/searchUserByEmail`, {
        method: "POST",
        data: { email: email.toLowerCase() },
      })
        .then((response) => {
          if (
            response?.data?.length > 0 &&
            response?.data[0]?.golfpad &&
            productId?.includes("student_lite")
          ) {
            input.coupon = "GP1876";
          }
        })
        .catch((error) => {
          console.log(error);
        });

      if (startDate) {
        input.startDate = startDate;
      }
      if (freeTrial) {
        input.freeTrial = freeTrial;
        if (
          window.location.href.includes("/offer") &&
          productId.includes("student_lite_annually")
        ) {
          input.coupon = "EFbQ8xhy";
        }
      }
      if (trialEnd) {
        input.trialEnd = trialEnd;
        if ((coupon || "").length === 0) {
          if (productId.includes("student_lite")) {
            input.coupon = "0V7yx6Tl";
          }
        }
      }
      if (
        window.location.href.includes("/mark-crossfield") &&
        productId.includes("student_lite")
      ) {
        input.coupon = "aNRQuT1C";
      }

      // Free trial promo from entered coupon
      if (FREE_TRIAL_PROMO_CODES.includes(coupon)) {
        if (productId.includes("student_lite")) {
          input.coupon = "";
          input.freeTrial = 31;
          localStorage.setItem("promoCode", coupon);
        } else {
          Swal.fire({
            title:
              '<p style="font-size:70%;">This coupon code is not applicable to the selected plan</p>',
            icon: "warning",
            confirmButtonText: "Ok",
            allowOutsideClick: true,
            customClass: {
              container: "my-swal",
            },
          });
          setIsSubmitting(false);
          return;
        }
      }

      // couponCode= in URL
      let couponCode = window.location.href;
      if (couponCode.includes("couponCode=")) {
        couponCode = couponCode.split("couponCode=")[1];
        if (FREE_TRIAL_PROMO_CODES.includes(couponCode)) {
          if (productId.includes("student_lite")) {
            setCoupon(couponCode);
            input.coupon = "";
            input.freeTrial = 31;
            setShowFreeTrialMessage(true);
            localStorage.setItem("promoCode", couponCode);
          } else {
            Swal.fire({
              title:
                '<p style="font-size:70%;">This coupon code is not applicable to the selected plan</p>',
              icon: "warning",
              confirmButtonText: "Ok",
              allowOutsideClick: true,
              customClass: {
                container: "my-swal",
              },
            });
            setIsSubmitting(false);
            return;
          }
        } else {
          input.coupon = couponCode;
          setCoupon(couponCode);
          localStorage.setItem("promoCode", couponCode);
        }
      }

      // REFERRALCODE in URL (overrides)
      let refRockCoupon = "";
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("REFERRALCODE")) {
        refRockCoupon = urlParams.get("REFERRALCODE");
      }
      if (refRockCoupon) {
        input.coupon = refRockCoupon.toUpperCase();
        setCoupon(refRockCoupon);
        localStorage.setItem("promoCode", refRockCoupon);
      }

      // Proration logic
      const userId = admin.auth().currentUser?.uid;
      const verifySubRes = await axiosWithToken(
        functionBaseUrl + `/api/verifySub/${userId}`,
        { method: "POST" }
      );

      const sub = verifySubRes.data.data;

      if (planProrationEligibility(sub?.productId, input.productId)) {
        const oldPlanPriceCents = plansEnvConfig[sub?.productId] * 100;

        const { startDate: prevStart, endDate: prevEnd } =
          dateExtractFromSub(sub);
        const credit = discountedPrice({
          previousSubStartDate: prevStart,
          previousSubEndDate: prevEnd,
          planPriceCents: oldPlanPriceCents,
        });
        const newPlanPriceCents = plansEnvConfig[input.productId] * 100;
        const proratedCents = Math.max(newPlanPriceCents - credit, 0);

        if (credit > 0) {
          input.overridePrice = proratedCents;
        }
      }

      // ✅ Create subscription NOW (not in init), with "active sub" handling
      const response = await createSubscriptionWithRetry(input);

      if (!response || response.status !== 200) {
        Swal.fire({
          title:
            '<p style="font-size:70%;">' +
            (response?.data?.showFreeTrialMessage ||
              response?.data?.message ||
              "An error occurred") +
            "</p>",
          icon: "warning",
          confirmButtonText: "Ok",
          allowOutsideClick: true,
          customClass: {
            container: "my-swal",
          },
        });
        setIsSubmitting(false);
        return;
      }

      setClientSecret(response.data.clientSecret);
      setSubscriptionId(response?.data?.subscription?.id);

      const amountDue =
        response?.data?.subscription?.latest_invoice?.amount_due || 0;
      const price = (amountDue / 100).toFixed(2);
      if (amountDue) {
        setSubscriptionAmount(price);
        if (handlePrice) {
          handlePrice(price);
        }
      } else {
        setSubscriptionAmount(0);
        if (handlePrice) {
          handlePrice(0);
        }
      }

      // Coupon & discount handling
      if (
        coupon &&
        !response?.data?.percentOff &&
        !response?.data?.amountOff &&
        planPrices.includes(String(price)) &&
        !FREE_TRIAL_PROMO_CODES.includes(coupon)
      ) {
        Swal.fire({
          title: '<p style="font-size:70%;">Invalid coupon</p>',
          icon: "warning",
          confirmButtonText: "Ok",
          allowOutsideClick: true,
          customClass: {
            container: "my-swal",
          },
        });
      } else {
        if (response.data.percentOff) {
          setDiscount(response.data.percentOff);
          setDiscountType("percent");
        } else if (response.data.amountOff) {
          setDiscount(response.data.amountOff);
          setDiscountType("amount");
        } else {
          setDiscount(0);
          if (coupon && !FREE_TRIAL_PROMO_CODES.includes(coupon)) {
            Swal.fire({
              title: '<p style="font-size:70%;">Invalid coupon</p>',
              icon: "warning",
              confirmButtonText: "Ok",
              allowOutsideClick: true,
              customClass: {
                container: "my-swal",
              },
            });
          }
        }
        if (coupon && !planPrices.includes(String(price))) {
          setCouponSuccess(true);
        }
      }

      if (!response.data.clientSecret) {
        Swal.fire({
          title:
            '<p style="font-size:70%;">' +
            response.data.showFreeTrialMessage +
            "</p>",
          icon: "warning",
          confirmButtonText: "Ok",
          allowOutsideClick: true,
          customClass: {
            container: "my-swal",
          },
        });
      }

      // If we used a saved card, subscription may already be paid
      if (useSavedCard && handleResult) {
        handleResult({
          amount: response?.data?.subscription?.latest_invoice?.amount_paid,
          id: response?.data?.subscription?.id,
        });
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        title:
          '<p style="font-size:70%;">An unexpected error occurred. Please try again.</p>',
        icon: "warning",
        confirmButtonText: "Ok",
        allowOutsideClick: true,
        customClass: {
          container: "my-swal",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    init();
  }, [productId]);

  if (loadingInit) {
    return (
      <div className="flex justify-center py-24">
        <Spinner color="primary500" size="md" />
      </div>
    );
  }

  return (
    <>
      {showDetails && (
        <SubscriptionDetails
          freeTrial={freeTrial}
          coupon={coupon}
          discount={discount}
          discountType={discountType}
          showFreeTrialMessage={showFreeTrialMessage}
          subscriptionAmount={subscriptionAmount}
          productId={productId}
        />
      )}

      {!hideCoupon && (
        <CouponInput
          init={init}
          coupon={coupon}
          setCoupon={setCoupon}
          setShowFreeTrialMessage={setShowFreeTrialMessage}
          discount={discount}
          discountType={discountType}
          productId={productId}
          subscriptionAmount={subscriptionAmount}
          couponSuccess={couponSuccess}
        />
      )}

      {card && (
        <div className="mt-4 gap-2 flex flex-col md:flex-row md:justify-between w-full max-w-[1000px]">
          <div className="text-black font-bold text-sm text-nowrap">
            Payment
          </div>
          <div className="text-black text-sm w-full max-w-[620px] flex items-center gap-5">
            <div className="flex items-center gap-1">
              {card.brand === "VISA" ? (
                <RiVisaLine className="size-7" />
              ) : card.brand === "MASTERCARD" ? (
                <RiMastercardLine className="size-7" />
              ) : (
                card.brand
              )}{" "}
              •••• •••• •••• {card.lastFour}
            </div>

            <FaPen
              className="cursor-pointer size-4 text-grayTrue500"
              onClick={() => {
                setUseSavedCard(false);
                setPaymentMethod("");
              }}
            />
          </div>
        </div>
      )}

      {/* For NEW card flow: click Subscribe to create subscription and get clientSecret */}
      {!useSavedCard && !clientSecret && (
        <button
          className="w-fit min-h-11 h-11 rounded-full font-semibold text-sm btnPrimary500 mt-6"
          onClick={subscribe}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Processing..." : buttonText || "Subscribe"}
        </button>
      )}

      {/* After subscription is created for new card, show Stripe Elements */}
      {clientSecret && !useSavedCard && (
        <div className="mt-6">
          <Elements
            options={{
              clientSecret,
              appearance: {
                theme: "stripe",
              },
            }}
            stripe={stripePromise}
          >
            <StripeSubscriptionForm
              clientSecret={clientSecret}
              subscriptionId={subscriptionId}
              buttonText={buttonText}
              handleResult={handleResult}
              fullName={fullName}
              email={email}
            />
          </Elements>
        </div>
      )}

      {/* Saved card flow: clicking Subscribe will create + pay subscription directly */}
      {useSavedCard && (
        <button
          className="w-fit min-h-11 h-11 rounded-full font-semibold text-sm btnPrimary500 mt-6"
          onClick={subscribe}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Processing..." : "Subscribe"}
        </button>
      )}
    </>
  );
}

export const StripeSubscription = StripeSubscriptionComponent;
